import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { MODULES } from "../utils";
import { LayoutDashboard, Users, ClipboardList, FileText, UserCog, Settings, LogOut, CalendarClock, Tag, KeyRound, ScrollText } from "lucide-react";
import { Toaster } from "./ui/sonner";
import ChangePassword from "./ChangePassword";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "superviseur"], testid: "nav-dashboard" },
  { to: "/ma-journee", label: "Ma journée", icon: CalendarClock, roles: ["employe"], testid: "nav-my-day" },
  { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "superviseur"], testid: "nav-clients" },
  { to: "/tarifs", label: "Liste des prix", icon: Tag, roles: ["admin", "superviseur"], testid: "nav-price-list" },
  { to: "/bons", label: "Bons de commande", icon: ClipboardList, roles: ["admin", "superviseur"], testid: "nav-orders" },
  { to: "/factures", label: "Factures", icon: FileText, roles: ["admin", "superviseur"], testid: "nav-invoices" },
  { to: "/employes", label: "Employés", icon: UserCog, roles: ["admin"], testid: "nav-employees" },
  { to: "/journal", label: "Journal", icon: ScrollText, roles: ["admin"], testid: "nav-journal" },
  { to: "/configuration", label: "Configuration", icon: Settings, roles: ["admin"], testid: "nav-settings" },
];

export function ModuleSelector() {
  const { module, setModule, user } = useApp();
  const isEmploye = user?.role === "employe";
  const entries = isEmploye && user.specialty
    ? [[user.specialty, MODULES[user.specialty]]]
    : [["tous", null], ...Object.entries(MODULES)];
  return (
    <div className="flex flex-wrap gap-2" data-testid="module-selector">
      {entries.map(([key, m]) => {
        const active = module === key;
        const label = m ? m.label : "Tous les services";
        return (
          <button
            key={key}
            data-testid={`module-selector-${key}`}
            onClick={() => setModule(key)}
            className={`px-3.5 py-1.5 rounded-md text-sm transition-colors border ${
              active ? "text-white border-transparent font-medium" : "bg-white text-slate-500 border-slate-200 hover:text-slate-900"
            }`}
            style={active ? { backgroundColor: m ? m.color : "#0F172A" } : {}}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, refresh } = useApp();
  const [pwOpen, setPwOpen] = useState(false);
  const [idleWarn, setIdleWarn] = useState(false);
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role));

  useEffect(() => {
    if (!user || user.role === "employe") return undefined;
    let stop = false;
    const check = async () => {
      try {
        const r = await api.get("/auth/session");
        if (!stop) setIdleWarn(r.data.idle_remaining_seconds != null && r.data.idle_remaining_seconds <= 300);
      } catch { /* 401 : l'intercepteur redirige vers /login avec le message */ }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [user]);

  const stay = async () => {
    try { await api.post("/auth/session/ping"); setIdleWarn(false); } catch { /* intercepteur */ }
  };
  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      <aside className="w-60 bg-white text-slate-700 flex-col border-r border-slate-200 shrink-0 select-none hidden md:flex" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="text-base font-semibold tracking-tight text-slate-900">Saphir Immo SA</div>
          <div className="text-xs text-slate-500 mt-0.5">Gestion & Facturation</div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-200">
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-xs text-slate-500 capitalize">{user.role}{user.specialty ? ` · ${MODULES[user.specialty]?.label}` : ""}</div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            className="mt-3 flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          >
            <LogOut size={14} /> Déconnexion
          </button>
          <button
            data-testid="change-password-button"
            onClick={() => setPwOpen(true)}
            className="mt-2 flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          >
            <KeyRound size={14} /> Changer mon mot de passe
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
          <ModuleSelector />
          <div className="md:hidden flex items-center gap-3">
            <span className="text-sm font-semibold">{user.name}</span>
            <button data-testid="logout-button-mobile" onClick={() => { logout(); navigate("/login"); }} className="text-slate-500"><LogOut size={16} /></button>
          </div>
        </header>
        {user.role === "employe" ? null : (
          <nav className="md:hidden bg-white border-b border-slate-200 px-3 py-2 flex gap-2 overflow-x-auto">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`${n.testid}-mobile`}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${isActive ? "bg-slate-900 text-white font-medium" : "text-slate-500 bg-white border border-slate-200"}`}>
                <n.icon size={13} /> {n.label}
              </NavLink>
            ))}
          </nav>
        )}
        {idleWarn && (
          <div data-testid="inactivity-banner" className="fixed top-0 inset-x-0 z-50 bg-amber-50 border-b border-amber-300 px-4 py-2.5 flex items-center justify-center gap-4">
            <span className="text-sm text-amber-900">Vous allez être déconnecté dans quelques minutes pour inactivité.</span>
            <button data-testid="stay-connected-button" onClick={stay}
              className="text-sm font-semibold bg-slate-900 text-white rounded-lg px-3 py-1.5 hover:bg-slate-800 transition-colors">
              Rester connecté
            </button>
          </div>
        )}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
      {user.must_change_password
        ? <ChangePassword forced onDone={refresh} />
        : pwOpen && <ChangePassword onDone={() => { setPwOpen(false); refresh(); }} />}
      <Toaster position="top-right" richColors />
    </div>
  );
}
